import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';

const PAIRS = [
  { key: 'USD', flag: '🇺🇸', label: 'ZMW / USD' },
  { key: 'EUR', flag: '🇪🇺', label: 'ZMW / EUR' },
  { key: 'GBP', flag: '🇬🇧', label: 'ZMW / GBP' },
  { key: 'ZAR', flag: '🇿🇦', label: 'ZMW / ZAR' },
];

interface RateData {
  rate: number;
  change: number;
  history: { day: string; rate: number }[];
}

// Returns YYYY-MM-DD for `daysAgo` days before today
function dateStr(daysAgo = 0) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

// Fetch one day's USD-base data from fawazahmed0 currency API, derive ZMW pairs
async function fetchDay(date: string): Promise<Record<string, number> | null> {
  try {
    const tag = date === dateStr(0) ? 'latest' : date;
    const url = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@${tag}/v1/currencies/usd.json`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const j = await res.json();
    const u = j.usd;
    return {
      USD: u.zmw,
      EUR: u.zmw / u.eur,
      GBP: u.zmw / u.gbp,
      ZAR: u.zmw / u.zar,
    };
  } catch {
    return null;
  }
}

async function fetchAllRates(): Promise<Record<string, RateData>> {
  const days = Array.from({ length: 7 }, (_, i) => ({
    label: new Date(dateStr(6 - i)).toLocaleDateString('en', { weekday: 'short' }),
    date: dateStr(6 - i),
  }));
  const snapshots = await Promise.all(days.map(d => fetchDay(d.date)));
  const result: Record<string, RateData> = {};
  for (const p of PAIRS) {
    const history = days
      .map((d, i) => ({ day: d.label, rate: snapshots[i]?.[p.key] ?? 0 }))
      .filter(h => h.rate > 0);
    const rate = history[history.length - 1]?.rate ?? 0;
    const prev = history[history.length - 2]?.rate ?? rate;
    result[p.key] = { rate, change: parseFloat((rate - prev).toFixed(4)), history };
  }
  return result;
}

export default function ExchangeRateCard() {
  const theme = useTheme();
  const [selected, setSelected] = React.useState(0);
  const [rates, setRates] = React.useState<Record<string, RateData> | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [updatedAt, setUpdatedAt] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllRates();
      setRates(data);
      setUpdatedAt(new Date().toLocaleTimeString());
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const activeKey = PAIRS[selected].key;
  const active = rates?.[activeKey];
  const isUp = (active?.change ?? 0) > 0;
  const isFlat = (active?.change ?? 0) === 0;
  const trendColor = isFlat
    ? theme.palette.text.secondary
    : isUp
    ? theme.palette.success.main
    : theme.palette.error.main;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Exchange Rates
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {updatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              Updated {updatedAt}
            </Typography>
          )}
          <IconButton size="small" onClick={load} disabled={loading} sx={{ p: 0.25 }}>
            <RefreshIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: '14px !important', '&:last-child': { pb: '14px !important' } }}>

          {/* Currency selector tabs */}
          <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexShrink: 0, flexWrap: 'wrap', gap: 0.75 }}>
            {PAIRS.map((p, i) => (
              <Chip
                key={p.key}
                label={p.key}
                size="small"
                onClick={() => setSelected(i)}
                variant={selected === i ? 'filled' : 'outlined'}
                color={selected === i ? 'primary' : 'default'}
                sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
              />
            ))}
          </Stack>

          {loading || !active ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/* Main rate display */}
              <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 1, flexShrink: 0 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    {PAIRS[selected].flag} &nbsp;{PAIRS[selected].label}
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                      {active.rate.toFixed(4)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ZMW</Typography>
                  </Stack>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: trendColor, justifyContent: 'flex-end' }}>
                    {isFlat ? <RemoveIcon fontSize="small" /> : isUp ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                    <Typography variant="body2" sx={{ fontWeight: 700, color: trendColor }}>
                      {isUp ? '+' : ''}{active.change.toFixed(4)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">vs prev. day</Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 1, flexShrink: 0 }} />

              {/* Sparkline chart */}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={active.history} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={trendColor} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={trendColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
                    <YAxis domain={['auto', 'auto']} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickFormatter={v => v.toFixed(2)} />
                    <Tooltip
                      formatter={(v: number) => [v.toFixed(4), PAIRS[selected].label]}
                      contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }}
                    />
                    <Area type="monotone" dataKey="rate" stroke={trendColor} strokeWidth={2} fill="url(#rateGradient)" dot={false} activeDot={{ r: 4, fill: trendColor }} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>

              {/* Bottom summary row */}
              <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={1} sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                {PAIRS.map((p, i) => {
                  const r = rates?.[p.key];
                  return (
                    <Box key={p.key} sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setSelected(i)}>
                      <Typography variant="caption" color={i === selected ? 'primary' : 'text.secondary'} sx={{ fontWeight: i === selected ? 700 : 400, display: 'block', fontSize: '0.65rem' }}>
                        {p.key}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                        {r?.rate.toFixed(2) ?? '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: (r?.change ?? 0) > 0 ? theme.palette.success.main : (r?.change ?? 0) < 0 ? theme.palette.error.main : 'text.secondary' }}>
                        {(r?.change ?? 0) > 0 ? '+' : ''}{r?.change.toFixed(2) ?? ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}

        </CardContent>
      </Card>
    </Box>
  );
}
